import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";

export const getReservationsSkill: Skill = {
  id: "getReservations",
  description:
    "Obtiene reservas de un restaurante para una fecha específica (default: hoy). Útil para preguntas como '¿qué reservas hay hoy?', '¿quién tiene mesa el viernes?'.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      date: {
        type: "string",
        description:
          "Fecha en formato ISO (YYYY-MM-DD). Si se omite, usa el día de hoy.",
      },
    },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) {
      return { ok: false, error: "Falta restaurantId." };
    }

    const dateStr =
      args.date && typeof args.date === "string"
        ? args.date.slice(0, 10)
        : new Date().toISOString().slice(0, 10);

    if (ctx.follow.enabled) {
      await ctx.follow.step(`Consultando reservas del ${dateStr}…`, {
        durationMs: 500,
      });
    }

    try {
      const params = new URLSearchParams();
      params.set("date", dateStr);
      const { data } = await apiWithAuth.get(
        `/restaurants/${restaurantId}/reservations?${params.toString()}`,
        ctx.accessToken,
      );
      const items: any[] = data?.items ?? data?.data ?? data ?? [];
      const reservations = items.slice(0, 30).map((r) => ({
        id: r.id,
        clientName: r.client?.name ?? r.clientName,
        phone: r.client?.phone ?? r.phone,
        time: r.startsAt ?? r.time,
        guests: r.guestCount ?? r.guests,
        tableName: r.table?.name,
        status: r.status,
        notes: r.notes,
      }));

      if (ctx.follow.enabled) {
        await ctx.follow.step(`✓ ${reservations.length} reserva(s)`, {
          durationMs: 500,
          spinner: false,
        });
        ctx.follow.clear();
      }

      return {
        ok: true,
        result: { date: dateStr, count: reservations.length, reservations },
        humanSummary: `${reservations.length} reserva(s) para ${dateStr}.`,
      };
    } catch (err) {
      return {
        ok: false,
        error: (err as Error)?.message ?? "Error obteniendo reservas.",
      };
    }
  },
};
